import {
  NgModule,
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuItem } from '../config';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { TooltipModule } from '../tooltip/tooltip';

@Component({
  selector: 'f-steps',
  template: `
    <section>
      <div class="section__content">
        <div class="form-secondary-header-bg">
          <div class="form-secondary-header">
            <div class="steppers-wrappers">
              <div
                [ngClass]="{ 'steppers p-steps p-component': true, 'p-readonly': readonly }"
                [ngStyle]="style"
                [class]="styleClass"
              >
                <ul class="steppers__path" role="tablist">
                  <li
                    *ngFor="let item of model; let i = index"
                    class="p-steps-item"
                    #menuitem
                    [ngStyle]="item.style"
                    [class]="item.styleClass || ''"
                    role="tab"
                    [attr.aria-selected]="i === activeIndex"
                    [attr.aria-expanded]="i === activeIndex"
                    fTooltip
                    [tooltipOptions]="item.tooltipOptions"
                    [ngClass]="{
                      completed: activeIndex > i,
                      'p-highlight p-steps-current active': isActive(item, i),
                      'p-disabled in-active': item.disabled || (!isActive(item, i) && activeIndex < i)
                    }"
                  >
                    <a
                      *ngIf="isClickableRouterLink(item); else elseBlock"
                      [routerLink]="item.routerLink"
                      [queryParams]="item.queryParams"
                      role="presentation"
                      [routerLinkActive]="'p-menuitem-link-active'"
                      [routerLinkActiveOptions]="item.routerLinkActiveOptions || { exact: false }"
                      class="p-menuitem-link"
                      (click)="itemClick($event, item, i)"
                      (keydown.enter)="itemClick($event, item, i)"
                      [target]="item.target || ''"
                      [attr.id]="item.id"
                      [attr.tabindex]="item.disabled || readonly ? null : item.tabindex ? item.tabindex : '0'"
                      [fragment]="item.fragment"
                      [queryParamsHandling]="item.queryParamsHandling"
                      [preserveFragment]="item.preserveFragment || false"
                      [skipLocationChange]="item.skipLocationChange || false"
                      [replaceUrl]="item.replaceUrl || false"
                      [state]="item.state"
                    >
                      <i *ngIf="item.icon" [class]="item.icon"></i>
                      <div class="content">
                        <!-- <span class="p-steps-number">{{ i + 1 }}</span> -->
                        <span class="p-steps-title" *ngIf="item.escape !== false; else htmlLabel">{{
                          item.label
                        }}</span>
                        <span *ngIf="item.status" class="state">
                          {{ item.status }}
                        </span>
                        <span *ngIf="!item.status" class="state">
                          {{ activeIndex > i ? 'Completed' : 'In Progress' }}
                        </span>
                        <ng-template #htmlLabel
                          ><span class="p-steps-title" [innerHTML]="item.label"></span
                        ></ng-template>
                      </div>
                    </a>
                    <ng-template #elseBlock>
                      <a
                        [attr.href]="item.url"
                        class="p-menuitem-link"
                        role="presentation"
                        (click)="itemClick($event, item, i)"
                        (keydown.enter)="itemClick($event, item, i)"
                        [target]="item.target"
                        [attr.id]="item.id"
                        [attr.tabindex]="
                          item.disabled || (i !== activeIndex && readonly) ? null : item.tabindex ? item.tabindex : '0'
                        "
                      >
                        <i *ngIf="item.icon" [class]="item.icon"></i>
                        <div class="content">
                          <!-- <span class="p-steps-number">{{ i + 1 }}</span> -->
                          <span class="p-steps-title" *ngIf="item.escape !== false; else htmlRouteLabel">{{
                            item.label
                          }}</span>
                          <span *ngIf="item.status" class="state">
                            {{ item.status }}
                          </span>
                          <span *ngIf="!item.status" class="state">
                            {{ activeIndex > i ? 'Completed' : activeIndex == i ? 'In Progress' : 'Pending' }}
                          </span>
                          <ng-template #htmlRouteLabel
                            ><span class="p-steps-title" [innerHTML]="item.label"></span
                          ></ng-template>
                        </div>
                      </a>
                    </ng-template>
                  </li>
                </ul>
              </div>
            </div>
            <div *ngIf="model.length > 1" class="form-actions">
              <!-- Single-Button -->
              <!-- do not remove ? in the ?.label -->
              <a
                [attr.data-interaction]="model[activeIndex]?.label + ' prev button'"
                *ngIf="activeIndex !== 0"
                (click)="this.prevStep.emit()"
                [ngClass]="{ disabled: readonly }"
                class="single-button--without-bg step-btns"
                >Previous</a
              >
              <a
                [attr.data-interaction]="model[activeIndex - 1]?.label + ' next button'"
                *ngIf="activeIndex !== model.length - 1"
                (click)="this.nextStep.emit()"
                [ngClass]="{ disabled: readonly }"
                class="single-button--with-bg step-btns"
                >Next</a
              >
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./steps.css'],
  host: {
    class: 'p-element',
  },
})
export class Steps implements OnInit, OnDestroy {
  @Input() activeIndex: number = 0;

  @Input() model!: MenuItem[];

  @Input() readonly: boolean = true;

  @Input() style: any;

  @Input() styleClass!: string;

  @Output() activeIndexChange: EventEmitter<any> = new EventEmitter();

  @Output() nextStep: EventEmitter<any> = new EventEmitter();

  @Output() prevStep: EventEmitter<any> = new EventEmitter();

  constructor(private router: Router, private route: ActivatedRoute, private cd: ChangeDetectorRef) {}

  subscription!: Subscription;

  ngOnInit() {
    this.subscription = this.router.events.subscribe(() => this.cd.markForCheck());
  }

  itemClick(event: Event, item: MenuItem, i: number) {
    if (this.readonly || item.disabled) {
      event.preventDefault();
      return;
    }

    this.activeIndexChange.emit(i);

    if (!item.url && !item.routerLink) {
      event.preventDefault();
    }

    if (item.command) {
      item.command({
        originalEvent: event,
        item: item,
        index: i,
      });
    }
  }

  isClickableRouterLink(item: MenuItem) {
    return item.routerLink && !this.readonly && !item.disabled;
  }

  isActive(item: MenuItem, index: number) {
    if (item.routerLink) {
      let routerLink = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];

      return this.router.isActive(this.router.createUrlTree(routerLink, { relativeTo: this.route }).toString(), false);
    }

    return index === this.activeIndex;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}

@NgModule({
  imports: [CommonModule, RouterModule, TooltipModule],
  exports: [Steps, RouterModule, TooltipModule],
  declarations: [Steps],
})
export class StepsModule {}
